import {
    Box,
    Flex,
    Group,
    Kbd,
    Loader,
    Stack,
    Text,
    UnstyledButton,
    type UnstyledButtonProps,
    VisuallyHidden,
} from "@mantine/core";
import { useDebouncedValue, useHotkeys, useInputState, useOs } from "@mantine/hooks";
import { Spotlight, type SpotlightActionData, spotlight } from "@mantine/spotlight";
import { Icon, iconArrowLeft, iconSearch } from "@surrealdb/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { usePageContext } from "vike-react/usePageContext";
import { getProductFromPath, PRODUCT_META } from "~/utils/product";
import { RateLimitError, SearchError, type SearchResult, searchDocs } from "~/utils/search";
import { SearchResultCard } from "./SearchResult";
import classes from "./style.module.scss";

function mapResultsToActions(results: SearchResult[], query: string): SpotlightActionData[] {
    return results.map((result, index) => ({
        id: `result-${index}`,
        label: String(result.title ?? ""),
        // A real anchor rather than a button-with-navigation: middle-click,
        // ctrl-click, copy-link and crawler semantics all come back, and the
        // browser owns the navigation. The click handler only reports.
        component: "a",
        href: String(result.url ?? "/"),
        onClick: () => {
            window.dataLayer?.push({
                event: "search_result_click",
                search_term: query,
                result_url: result.url,
            });
        },
        children: (
            <SearchResultCard
                result={result}
                query={query}
            />
        ),
    })) as SpotlightActionData[];
}

export function SearchDocs(props: UnstyledButtonProps) {
    const [search, setSearch] = useInputState("");
    const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
    const [rateLimitRemaining, setRateLimitRemaining] = useState(0);
    const os = useOs();
    const { urlPathname } = usePageContext();
    const product = getProductFromPath(urlPathname);

    const hasTypedQuery = search.trim().length > 0;

    const searchQuery = useQuery({
        queryKey: ["docs-search", debouncedSearch, product] as const,
        queryFn: async ({ signal }) => {
            const results = await searchDocs(debouncedSearch, signal, product);

            window.dataLayer?.push({
                event: "search_query",
                search_term: debouncedSearch,
                search_product: product,
            });

            // Surface queries that find nothing so content gaps are
            // measurable rather than inferred from missing clicks.
            if (results.length === 0) {
                window.dataLayer?.push({
                    event: "search_no_results",
                    search_term: debouncedSearch,
                    search_product: product,
                });
            }

            return results;
        },
        enabled: debouncedSearch.length > 0,
        placeholderData: keepPreviousData,
        retry: false,
    });

    const openSearch = useCallback(() => {
        spotlight.open();
        window.dataLayer?.push({ event: "search_opened" });
    }, []);

    useHotkeys([["mod+K", openSearch]]);

    useEffect(() => {
        if (rateLimitRemaining <= 0) return;
        const timer = setTimeout(() => setRateLimitRemaining((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [rateLimitRemaining]);

    useEffect(() => {
        if (searchQuery.error instanceof RateLimitError) {
            setRateLimitRemaining(searchQuery.error.retryAfterSeconds ?? 10);
        } else if (!searchQuery.error) {
            setRateLimitRemaining(0);
        }
    }, [searchQuery.error]);

    const loading = hasTypedQuery && (search.trim() !== debouncedSearch || searchQuery.isFetching);

    const errorMessage =
        searchQuery.error instanceof RateLimitError
            ? null
            : searchQuery.error instanceof SearchError
              ? searchQuery.error.message
              : searchQuery.error
                ? "Something went wrong - please try again"
                : null;

    const actions = useMemo(() => {
        if (!hasTypedQuery || debouncedSearch.length === 0) return [];
        return mapResultsToActions(searchQuery.data ?? [], debouncedSearch);
    }, [debouncedSearch, hasTypedQuery, searchQuery.data]);

    const modKey = os === "macos" ? "⌘" : "Ctrl";
    const hasQuery = hasTypedQuery;

    let nothingFound: ReactNode;

    if (rateLimitRemaining > 0) {
        nothingFound = `Too many requests - try again in ${rateLimitRemaining}s`;
    } else if (errorMessage) {
        nothingFound = errorMessage;
    } else if (searchQuery.isSuccess && debouncedSearch.length > 0 && actions.length === 0) {
        nothingFound = "No results found";
    } else {
        nothingFound = <SearchTutorial modKey={modKey} />;
    }

    return (
        <>
            <UnstyledButton
                className={classes.search}
                aria-label="Search documentation"
                py={8}
                px="md"
                mb="md"
                w="100%"
                onClick={openSearch}
                {...props}
            >
                <Group gap="sm">
                    <Icon
                        path={iconSearch}
                        size="sm"
                    />
                    <Text fz="sm">Search the docs</Text>
                    <Flex
                        align="center"
                        gap={4}
                        ml="auto"
                    >
                        <Kbd size="xs">{modKey}</Kbd>
                        <Kbd size="xs">K</Kbd>
                    </Flex>
                </Group>
            </UnstyledButton>
            {/* Announces result counts to screen readers; the list itself
                re-renders silently otherwise. */}
            <VisuallyHidden
                role="status"
                aria-live="polite"
            >
                {hasQuery && searchQuery.isSuccess
                    ? `${actions.length} result${actions.length === 1 ? "" : "s"} for ${debouncedSearch}`
                    : ""}
            </VisuallyHidden>
            {/* Composed rather than the `<Spotlight>` shorthand, which renders
                the search, the list and the empty state and nothing else -
                the scope line needs a footer beneath them. */}
            <Spotlight.Root
                scrollable
                maxHeight={500}
                transitionProps={{ transition: "fade-down" }}
                overlayProps={{ blur: 0, backgroundOpacity: 0.5 }}
                classNames={{
                    inner: classes.searchScreen,
                    body: classes.searchBody,
                    actionsList: classes.searchList,
                    action: classes.searchAction,
                    content: classes.searchContent,
                    search: classes.searchInput,
                    empty: classes.searchEmpty,
                }}
            >
                {/* The index is shared but the query is scoped to whichever
                    product the reader is currently in, so the panel names the
                    one it is searching rather than leaving another product's
                    page looking like a gap in the results. */}
                <Box className={classes.searchScope}>
                    <Text
                        fz="xs"
                        c="dimmed"
                    >
                        Searching{" "}
                        <Text
                            span
                            inherit
                            c="bright"
                            fw={500}
                        >
                            {PRODUCT_META[product].label}
                        </Text>{" "}
                        documentation
                    </Text>
                </Box>
                <Spotlight.Search
                    placeholder="Search the docs"
                    leftSection={loading ? <Loader size="xs" /> : <Icon path={iconSearch} />}
                    autoFocus
                    onChange={setSearch}
                    value={search}
                    className={classes.searchInput}
                    rightSection={<Kbd>Esc</Kbd>}
                    mod={{ expanded: actions.length > 0 && hasQuery }}
                />
                {actions.length > 0 ? (
                    <Spotlight.ActionsList>
                        {actions.map(({ id, ...action }) => (
                            <Spotlight.Action
                                key={id}
                                {...action}
                            />
                        ))}
                    </Spotlight.ActionsList>
                ) : (
                    <Spotlight.Empty>{nothingFound}</Spotlight.Empty>
                )}
            </Spotlight.Root>
        </>
    );
}

function SearchTutorial({ modKey }: { modKey: string }) {
    return (
        <Stack
            py={90}
            align="center"
        >
            <Text
                fz="lg"
                ta="center"
            >
                Enter a search term to find documentation
            </Text>
            {/* Both columns size to their content, so the block's real width
                is its visual width and centring it lines it up under the
                heading - a half-and-half split centres the gap instead. */}
            <Box
                mt="xl"
                className={classes.tutorialGrid}
            >
                <Kbd>{modKey} + K</Kbd>
                <Text>to open the search</Text>
                <Kbd>
                    <Icon
                        path={iconArrowLeft}
                        style={{ transform: "rotate(-90deg)" }}
                    />
                </Kbd>
                <Text>to navigate down</Text>
                <Kbd>
                    <Icon
                        path={iconArrowLeft}
                        style={{ transform: "rotate(90deg)" }}
                    />
                </Kbd>
                <Text>to navigate up</Text>
                <Kbd>Esc</Kbd>
                <Text>to close the search</Text>
            </Box>
        </Stack>
    );
}
